import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle, Check, Loader2, LayoutDashboard, BarChart3, Layers, Filter,
  PencilLine, RotateCcw, Save, Plus, Trash2, GripVertical, ArrowUp, ArrowDown,
  History, RefreshCw, Settings2, ChevronDown, ChevronRight, MessageCircle, Copy,
  Inbox, X,
} from 'lucide-react';
// GridLayout + Move icon removed with WidgetGridCanvas extraction (iter-222)
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit } from '../lib/embedChannel';
import { useLiveWidgets } from '../lib/useLiveWidgets';
import { LiveRefreshBadge } from '../components/embed/LiveRefreshBadge';
import { LiveDataMiniChart } from '../components/embed/LiveDataMiniChart';
import { WidgetLivePreview } from '../components/embed/WidgetLivePreview';
import { PresetMenu } from '../components/embed/PresetMenu';
import { WidgetGridCanvas } from '../components/embed/WidgetGridCanvas';
import { PageNavChrome, isPageVisible, normalizeNavStyle } from '../components/dashboards/PageNavChrome';

/**
 * Embed Content Viewer (iframe surface).
 *
 * Mounted at `/embed/v2/view/:resource/:id`. Reads the JWT via
 * `useEmbedToken` (postMessage bootstrap or legacy `?token=`), fetches
 * the workspace-scoped content, and renders a lightweight preview.
 *
 * P1 authoring: when the resolved embed session includes the `write`
 * capability, the header row is swapped for editable name/description
 * inputs plus a Save/Reset toolbar. For dashboards, each widget row
 * becomes editable — title inline, delete button, and an "Add widget"
 * action creates a new KPI tile. Save PUTs the full doc back through
 * the same legacy routes admin UIs use (PUT /api/dashboards/:id,
 * PUT /api/reports/:id), scoped by the JWT so the host can't
 * accidentally cross workspaces. All postMessage traffic flows
 * through `embedChannel`.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

// Widget types the inline "Add widget" button can seed. Kept short —
// authors switch types via the full canvas editor once they own the
// tile; this list is deliberately just the "safe" starter shapes.
const WIDGET_TYPE_OPTIONS = [
  { value: 'kpi', label: 'KPI card' },
  { value: 'bar_chart', label: 'Bar chart' },
  { value: 'line_chart', label: 'Line chart' },
  { value: 'table', label: 'Table' },
];

// Fresh widget seed (unique id per call). New widgets stack below the
// existing grid rather than colliding at 0,0 — mirrors what the full
// dashboard editor does.
function _newWidget(existing, type = 'kpi') {
  const maxY = (existing || []).reduce((m, w) => Math.max(m, (w.y || 0) + (w.h || 3)), 0);
  return {
    widget_id: `w_${Math.random().toString(36).slice(2, 10)}`,
    widget_type: type,
    title: 'Untitled widget',
    config: {},
    x: 0,
    y: maxY,
    w: 4,
    h: 3,
  };
}

// Layout presets — one-click starters so blank canvases turn into
// usable dashboards. Each preset returns a widgets[] array bound to
// the CURRENT edit state (so if some measures are already picked, they
// carry over into the seeded tiles when possible).
const LAYOUT_PRESETS = [
  {
    id: 'kpis-row',
    label: '4 KPIs across',
    description: 'Row of four KPI cards',
    build: () => ([
      { widget_type: 'kpi', title: 'KPI 1', x: 0, y: 0, w: 3, h: 3 },
      { widget_type: 'kpi', title: 'KPI 2', x: 3, y: 0, w: 3, h: 3 },
      { widget_type: 'kpi', title: 'KPI 3', x: 6, y: 0, w: 3, h: 3 },
      { widget_type: 'kpi', title: 'KPI 4', x: 9, y: 0, w: 3, h: 3 },
    ]),
  },
  {
    id: 'kpis-plus-chart',
    label: '3 KPIs + big chart',
    description: 'Three KPIs on top, a wide chart underneath',
    build: () => ([
      { widget_type: 'kpi',       title: 'Headline 1', x: 0, y: 0, w: 4, h: 3 },
      { widget_type: 'kpi',       title: 'Headline 2', x: 4, y: 0, w: 4, h: 3 },
      { widget_type: 'kpi',       title: 'Headline 3', x: 8, y: 0, w: 4, h: 3 },
      { widget_type: 'bar_chart', title: 'Trend',     x: 0, y: 3, w: 12, h: 5 },
    ]),
  },
  {
    id: 'split-charts',
    label: 'Two-column charts',
    description: 'Bar + line side by side, with a table below',
    build: () => ([
      { widget_type: 'bar_chart',  title: 'Category breakdown', x: 0, y: 0, w: 6, h: 5 },
      { widget_type: 'line_chart', title: 'Trend over time',    x: 6, y: 0, w: 6, h: 5 },
      { widget_type: 'table',      title: 'Detail rows',        x: 0, y: 5, w: 12, h: 4 },
    ]),
  },
  {
    id: 'exec-summary',
    label: 'Executive summary',
    description: 'Big KPI + supporting KPIs + chart',
    build: () => ([
      { widget_type: 'kpi',        title: 'Primary metric',   x: 0, y: 0, w: 6, h: 4 },
      { widget_type: 'kpi',        title: 'Supporting 1',     x: 6, y: 0, w: 3, h: 2 },
      { widget_type: 'kpi',        title: 'Supporting 2',     x: 9, y: 0, w: 3, h: 2 },
      { widget_type: 'kpi',        title: 'Supporting 3',     x: 6, y: 2, w: 3, h: 2 },
      { widget_type: 'kpi',        title: 'Supporting 4',     x: 9, y: 2, w: 3, h: 2 },
      { widget_type: 'line_chart', title: 'Trend',            x: 0, y: 4, w: 12, h: 4 },
    ]),
  },
];

function _fromPreset(preset) {
  return preset.build().map(w => ({
    widget_id: `w_${Math.random().toString(36).slice(2, 10)}`,
    config: {},
    ...w,
  }));
}

const RESOURCE_META = {
  dashboard: {
    icon: LayoutDashboard,
    collectionPath: 'dashboards',
    widgetKey: 'widgets',
    label: 'Dashboard',
    tone: 'text-indigo-400',
    supportsWidgets: true,
    // Fields sent when persisting through PUT /api/dashboards/:id.
    // Widgets come from the local edits state so the toolbar reflects
    // add/rename/delete without a refetch round-trip.
    savePayload: (content, edits) => ({
      name: edits.name,
      description: edits.description,
      widgets: Array.isArray(edits.widgets) ? edits.widgets : [],
      tabs: Array.isArray(content.tabs) ? content.tabs : [],
      grid_snap: content.grid_snap || 1,
      nav_style: content.nav_style || 'top_tabs',
    }),
    idField: 'dashboard_id',
  },
  report: {
    icon: BarChart3,
    collectionPath: 'reports',
    widgetKey: 'visuals',
    label: 'Report',
    tone: 'text-emerald-400',
    supportsWidgets: false,
    // Report PUT accepts an unstructured body; forward the fields the
    // server persists back verbatim so we don't stomp existing config.
    savePayload: (content, edits) => ({
      name: edits.name,
      description: edits.description,
      visualization_type: content.visualization_type || 'table',
      query_definition: content.query_definition || {},
      parameters: content.parameters || [],
      config: content.config || {},
    }),
    idField: 'report_id',
  },
};

// Value-equal comparison for the widget list. We can't just JSON-
// stringify because widget objects can hold config blobs with
// non-deterministic key order after a save round-trip; but for
// dirty-detection a stringified compare is good enough because
// server-managed keys survive verbatim through our own writes.
function _widgetsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
  if (a.length !== b.length) return false;
  try { return JSON.stringify(a) === JSON.stringify(b); }
  catch { return false; }
}

export default function EmbedContentViewerPage() {
  const { resource, id } = useParams();
  const token = useEmbedToken();
  const [content, setContent] = useState(null);
  const [claims, setClaims] = useState(null);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Authoring state — mirrors the server-side name/description/widgets
  // so a "Reset" button can discard local edits without a refetch.
  const [edits, setEdits] = useState({ name: '', description: '', widgets: [] });
  const [savedSnapshot, setSavedSnapshot] = useState({ name: '', description: '', widgets: [] });
  const [saveState, setSaveState] = useState({ status: 'idle', message: null });
  // Optional annotation the author attaches to the next save. Cleared
  // after a successful save so it can't accidentally leak into the
  // next unrelated change.
  const [saveNote, setSaveNote] = useState('');

  // Create-from-scratch: when the route id is the literal "new", we render
  // an empty draft in edit mode and the first Save POSTs to create it,
  // after which `newId` holds the real id and later Saves PUT as usual.
  const [newId, setNewId] = useState(null);
  // Pages — active page for multi-page dashboard navigation in the viewer.
  const [embedPageId, setEmbedPageId] = useState(null);
  const isNew = id === 'new';
  const effectiveId = isNew ? newId : id;

  const meta = RESOURCE_META[resource];

  const canWrite = useMemo(
    () => Array.isArray(claims?.capabilities) && claims.capabilities.includes('write'),
    [claims],
  );
  const isDirty = useMemo(() => (
    (isNew && !newId)
      || edits.name !== savedSnapshot.name
      || edits.description !== savedSnapshot.description
      || !_widgetsEqual(edits.widgets, savedSnapshot.widgets)
  ), [edits, savedSnapshot, isNew, newId]);

  useEffect(() => {
    if (!meta) { setError(`Unknown resource: ${resource}`); setLoading(false); return; }
    if (!token) { setError('Missing ?token= in URL'); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        // Fetch branding first (fire-and-forget) so the shell can render
        // in the host colors even before content resolves.
        fetch(`${API}/api/embed/v2/branding`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
          .then(r => (r.ok ? r.json() : null))
          .then(b => { if (alive && b?.branding) setBranding(b.branding); })
          .catch(() => { /* branding is best-effort */ });

        // Create-from-scratch: no content to fetch — pull claims from
        // /authenticate so canWrite works, then seed an empty draft.
        if (isNew) {
          const ar = await fetch(`${API}/api/embed/v2/authenticate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!ar.ok) {
            const b = await ar.json().catch(() => ({}));
            throw new Error(b?.detail?.message || `HTTP ${ar.status}`);
          }
          const ab = await ar.json();
          if (!alive) return;
          setClaims({ capabilities: ab.capabilities || ['read'], roles: ab.roles || [], sub: ab.sub });
          const seed = { name: resource === 'report' ? 'Untitled report' : 'Untitled dashboard', description: '', widgets: [] };
          setContent({});
          setEdits(seed);
          setSavedSnapshot({ name: '', description: '', widgets: [] });
          emit('content.new', { resource, workspace_id: ab.workspace_id });
          return;
        }

        const url = `${API}/api/embed/v2/${meta.collectionPath}/${encodeURIComponent(id)}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b?.detail?.message || `HTTP ${res.status}`);
        }
        const body = await res.json();
        if (!alive) return;
        setContent(body.content);
        setClaims(body.claims);
        const seed = {
          name: body.content?.name || body.content?.title || '',
          description: body.content?.description || '',
          widgets: Array.isArray(body.content?.widgets) ? body.content.widgets : [],
        };
        setEdits(seed);
        setSavedSnapshot(seed);
        emit('content.loaded', { resource, id, workspace_id: body.workspace_id });
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
        emit('error', { resource, id, message: String(e?.message || e) });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token, resource, id, meta]);

  // ─── Semantic catalog (widget data binding) ──────────────────────────
  // Fetched once per session; used by the widget rows to render
  // measure/dimension dropdowns bound into `widget.config`.
  const [catalog, setCatalog] = useState(null); // { measures, dimensions } or null
  useEffect(() => {
    if (!token || !meta?.supportsWidgets) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/embed/v2/semantic-catalog`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!alive) return;
        setCatalog({
          measures: Array.isArray(body?.measures) ? body.measures : [],
          dimensions: Array.isArray(body?.dimensions) ? body.dimensions : [],
        });
      } catch {
        // Catalog is best-effort — the widget row still works with
        // freeform config; we just don't offer a picker.
        if (alive) setCatalog({ measures: [], dimensions: [] });
      }
    })();
    return () => { alive = false; };
  }, [token, meta]);

  const handleSave = async () => {
    if (!canWrite || !isDirty || !meta) return;
    setSaveState({ status: 'saving', message: null });
    try {
      // ── Create path: first save of a brand-new draft ────────────────
      if (isNew && !newId) {
        const createBody = resource === 'report'
          ? { name: edits.name, description: edits.description, query: '', visualization_type: 'table' }
          : { name: edits.name, description: edits.description, widgets: Array.isArray(edits.widgets) ? edits.widgets : [] };
        const cres = await fetch(`${API}/api/embed/v2/${meta.collectionPath}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(createBody),
        });
        if (!cres.ok) {
          const b = await cres.json().catch(() => ({}));
          throw new Error(typeof b?.detail === 'string' ? b.detail : (b?.detail?.message || `HTTP ${cres.status}`));
        }
        const cbody = await cres.json();
        const created = cbody.content;
        const createdId = created[meta.idField];
        setNewId(createdId);
        setContent(created);
        const cseed = {
          name: created.name || edits.name,
          description: created.description || edits.description,
          widgets: Array.isArray(created?.widgets) ? created.widgets : (edits.widgets || []),
        };
        setEdits(cseed);
        setSavedSnapshot(cseed);
        setSaveState({ status: 'saved', message: 'Created' });
        setSaveNote('');
        emit('content.created', { resource, id: createdId, workspace_id: cbody.workspace_id });
        return;
      }

      const payload = meta.savePayload(content || {}, edits);
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      // Only forward the save note when the author actually filled it
      // in — keeps the header off the wire for the common case.
      const trimmedNote = (saveNote || '').trim();
      if (trimmedNote) headers['X-Save-Note'] = trimmedNote.slice(0, 280);
      const res = await fetch(`${API}/api/${meta.collectionPath}/${encodeURIComponent(effectiveId)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const detail = b?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message || `HTTP ${res.status}`);
        throw new Error(msg);
      }
      const updated = await res.json();
      // Keep the full doc around so subsequent saves keep server-managed
      // fields (config, updated_at, etc.) intact.
      setContent(updated);
      const seed = {
        name: edits.name,
        description: edits.description,
        widgets: Array.isArray(updated?.widgets) ? updated.widgets : edits.widgets,
      };
      // Server may have re-hydrated ordering/normalization; align local
      // state with the authoritative response before flipping the dirty
      // detector back off.
      setEdits(seed);
      setSavedSnapshot(seed);
      setSaveState({ status: 'saved', message: 'Saved' });
      // Clear the note now that it's landed in history — prevents it
      // from silently attaching to an unrelated future save.
      setSaveNote('');
      emit('content.saved', {
        resource,
        id,
        name: seed.name,
        widget_count: seed.widgets.length,
        updated_at: updated?.updated_at,
      });
      setTimeout(() => setSaveState(s => (s.status === 'saved' ? { status: 'idle', message: null } : s)), 1600);
    } catch (e) {
      const message = String(e?.message || e);
      setSaveState({ status: 'error', message });
      emit('content.save_failed', { resource, id, message });
    }
  };

  const handleReset = () => {
    setEdits(savedSnapshot);
    setSaveState({ status: 'idle', message: null });
  };

  const patchWidget = (widget_id, patch) => {
    setEdits(s => ({
      ...s,
      widgets: s.widgets.map(w => (w.widget_id === widget_id ? { ...w, ...patch } : w)),
    }));
  };
  const removeWidget = (widget_id) => {
    setEdits(s => ({ ...s, widgets: s.widgets.filter(w => w.widget_id !== widget_id) }));
  };
  const addWidget = () => {
    setEdits(s => ({ ...s, widgets: [...s.widgets, _newWidget(s.widgets)] }));
  };
  // Clone an existing widget: deep-copy config + shift the new tile one
  // row down so it doesn't overlap the source.
  const duplicateWidget = (widget_id) => {
    setEdits(s => {
      const src = s.widgets.find(w => w.widget_id === widget_id);
      if (!src) return s;
      const clone = {
        ...JSON.parse(JSON.stringify(src)),
        widget_id: `w_${Math.random().toString(36).slice(2, 10)}`,
        title: `${src.title || 'Untitled'} (copy)`,
        y: (src.y || 0) + (src.h || 3),
      };
      return { ...s, widgets: [...s.widgets, clone] };
    });
  };
  // Apply a layout preset. Deliberately REPLACES the current widgets
  // rather than appending — the whole point of a preset is a starter,
  // not an incremental tweak. Gated on empty canvas OR explicit
  // confirm inside the picker to avoid surprising users. Supports
  // both built-in presets (kind='builtin') and workspace-defined
  // custom presets (kind='custom').
  const [customPresets, setCustomPresets] = useState([]);
  useEffect(() => {
    if (!token || !meta?.supportsWidgets) return undefined;
    let alive = true;
    fetch(`${API}/api/embed/v2/layout-presets`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(body => { if (alive && Array.isArray(body?.data)) setCustomPresets(body.data); })
      .catch(() => { /* best-effort */ });
    return () => { alive = false; };
  }, [token, meta]);
  const applyPreset = (kind, presetId) => {
    if (kind === 'custom') {
      const cp = customPresets.find(p => p.preset_id === presetId);
      if (!cp) return;
      const widgets = (cp.widgets || []).map(w => ({
        widget_id: `w_${Math.random().toString(36).slice(2, 10)}`,
        config: {},
        ...w,
      }));
      setEdits(s => ({ ...s, widgets }));
      emit('layout.preset_applied', { preset_id: presetId, kind: 'custom', widget_count: widgets.length });
      return;
    }
    const preset = LAYOUT_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setEdits(s => ({ ...s, widgets: _fromPreset(preset) }));
    emit('layout.preset_applied', { preset_id: presetId, kind: 'builtin', widget_count: preset.build().length });
  };

  const saveAsPreset = async (label) => {
    if (!label || !token || !canWrite) return;
    // Strip local-only fields (widget_id) — the server assigns fresh
    // ids each time the preset is applied.
    const widgets = edits.widgets.map(({ widget_id, ...rest }) => rest);
    try {
      const res = await fetch(`${API}/api/embed/v2/layout-presets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ label, widgets }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail || `HTTP ${res.status}`);
      }
      const row = await res.json();
      setCustomPresets(s => [row, ...s]);
      emit('layout.preset_saved', { preset_id: row.preset_id, label: row.label });
    } catch (e) {
      window.alert(`Could not save preset: ${String(e?.message || e)}`);
    }
  };
  // Drag/Resize handler — bridges react-grid-layout's layout events
  // (each item: { i, x, y, w, h }) into `edits.widgets`. Layout items
  // use `i === widget_id`.
  const onGridLayoutChange = useCallback((layout) => {
    setEdits(s => {
      const byId = new Map(layout.map(l => [l.i, l]));
      const next = s.widgets.map(w => {
        const l = byId.get(w.widget_id);
        if (!l) return w;
        // Only mutate when the coords actually changed — prevents an
        // "identity update" from marking the doc dirty on first paint.
        if (l.x === w.x && l.y === w.y && l.w === w.w && l.h === w.h) return w;
        return { ...w, x: l.x, y: l.y, w: l.w, h: l.h };
      });
      return { ...s, widgets: next };
    });
  }, []);
  // Reorder: swap adjacent widget positions in the array. We keep this
  // as an array-order change only; the underlying `x/y` layout stays
  // untouched so a rollback (or the full canvas editor) can still
  // honor the author's spatial choices.
  const moveWidget = (widget_id, direction) => {
    setEdits(s => {
      const list = [...s.widgets];
      const idx = list.findIndex(w => w.widget_id === widget_id);
      if (idx < 0) return s;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= list.length) return s;
      [list[idx], list[target]] = [list[target], list[idx]];
      return { ...s, widgets: list };
    });
  };

  // ─── Version history ──────────────────────────────────────────────────
  // Kept in a separate slice from `edits`. Fetched lazily on toggle so
  // read-only sessions never pay the round-trip.
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState(null); // null → not fetched yet
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoreState, setRestoreState] = useState({ status: 'idle', message: null });

  const fetchVersions = useCallback(async () => {
    if (!meta?.supportsWidgets || !token) return;
    setVersionsLoading(true);
    try {
      const url = `${API}/api/embed/v2/${meta.collectionPath}/${encodeURIComponent(id)}/versions?limit=10`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setVersions(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setVersions([]);
      setRestoreState({ status: 'error', message: String(e?.message || e) });
    } finally {
      setVersionsLoading(false);
    }
  }, [meta, token, id]);

  const toggleHistory = () => {
    setShowHistory(v => {
      const next = !v;
      // Lazy-load on first open (or refresh whenever we open after a save).
      if (next) fetchVersions();
      return next;
    });
  };

  const handleRestore = async (versionId) => {
    if (!canWrite || !token || !meta?.supportsWidgets) return;
    setRestoreState({ status: 'restoring', message: null });
    try {
      const url = `${API}/api/embed/v2/${meta.collectionPath}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const detail = b?.detail;
        throw new Error(typeof detail === 'string' ? detail : (detail?.message || `HTTP ${res.status}`));
      }
      const body = await res.json();
      const restored = body?.content || {};
      setContent(restored);
      const seed = {
        name: restored.name || '',
        description: restored.description || '',
        widgets: Array.isArray(restored.widgets) ? restored.widgets : [],
      };
      setEdits(seed);
      setSavedSnapshot(seed);
      setRestoreState({ status: 'restored', message: 'Restored' });
      // Refresh versions — the restore itself created a new snapshot.
      fetchVersions();
      emit('content.restored', {
        resource, id,
        from_version_id: versionId,
        widget_count: seed.widgets.length,
      });
      setTimeout(() => setRestoreState(s => (s.status === 'restored' ? { status: 'idle', message: null } : s)), 1600);
    } catch (e) {
      setRestoreState({ status: 'error', message: String(e?.message || e) });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="embed-viewer-error">
        <div className="max-w-md rounded-lg border border-rose-500/40 bg-rose-500/5 p-5 text-center">
          <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto mb-2" />
          <div className="text-sm font-semibold mb-1">Couldn&apos;t load {resource}</div>
          <div className="text-xs text-muted-foreground break-words">{error}</div>
        </div>
      </div>
    );
  }

  if (loading || !content || !meta) {
    return (
      <div className="min-h-screen flex items-center gap-2 justify-center p-6 bg-background text-xs text-muted-foreground" data-testid="embed-viewer-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {resource}…
      </div>
    );
  }

  const Icon = meta.icon;
  const allWidgets = canWrite && meta.supportsWidgets
    ? edits.widgets
    : (Array.isArray(content?.[meta.widgetKey]) ? content[meta.widgetKey] : []);
  // Pages — dashboards can have multiple pages (tabs). Viewers only see
  // visible pages and land on the first one; a legacy single-canvas
  // dashboard (no tabs) renders every widget with no navigation.
  const pageNavStyle = normalizeNavStyle(content?.nav_style);
  const allPages = resource === 'dashboard' && Array.isArray(content?.tabs) ? content.tabs : [];
  const visiblePages = canWrite ? allPages : allPages.filter(isPageVisible);
  const multiPage = visiblePages.length > 1;
  const firstPageId = visiblePages[0]?.tab_id ?? null;
  const activePageId = (multiPage && visiblePages.some(p => p.tab_id === embedPageId))
    ? embedPageId : firstPageId;
  const widgetsToRender = multiPage
    ? allWidgets.filter(w => (w.tab_id || firstPageId) === activePageId)
    : allWidgets;
  const filterCount = Array.isArray(content?.filters) ? content.filters.length : 0;
  const productName = brandingProductName(branding, meta.label);

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      data-testid="embed-viewer-page"
      style={brandingRootStyle(branding)}
    >
      <div className="max-w-4xl mx-auto p-5">
        <div className="flex items-start gap-3 mb-4 pb-3 border-b border-border/25">
          {branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt=""
              className="h-8 w-auto max-w-[140px] object-contain shrink-0 mt-0.5"
              data-testid="embed-viewer-logo"
            />
          ) : (
            <Icon className={`w-6 h-6 mt-0.5 ${meta.tone}`} />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div
                className="text-[10px] uppercase tracking-widest text-muted-foreground"
                data-testid="embed-viewer-product-name"
              >
                {productName}
              </div>
              {canWrite && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-indigo-400/40 bg-indigo-500/10 px-1.5 py-[1px] text-[9px] uppercase tracking-wider text-indigo-300"
                  data-testid="embed-viewer-write-badge"
                >
                  <PencilLine className="w-2.5 h-2.5" /> Editable
                </span>
              )}
            </div>
            {canWrite ? (
              <input
                className="w-full mt-1 bg-transparent text-lg font-semibold outline-none border-b border-transparent focus:border-border/50 py-0.5"
                value={edits.name}
                placeholder={`(untitled ${resource})`}
                onChange={(e) => setEdits(s => ({ ...s, name: e.target.value }))}
                data-testid="embed-viewer-name-input"
                aria-label={`${meta.label} name`}
              />
            ) : (
              <div className="text-lg font-semibold truncate">{content.name || content.title || `(untitled ${resource})`}</div>
            )}
            {canWrite ? (
              <textarea
                className="w-full mt-1 bg-transparent text-xs text-muted-foreground outline-none border-b border-transparent focus:border-border/50 resize-none py-0.5"
                rows={2}
                value={edits.description || ''}
                placeholder="Add a description…"
                onChange={(e) => setEdits(s => ({ ...s, description: e.target.value }))}
                data-testid="embed-viewer-description-input"
                aria-label={`${meta.label} description`}
              />
            ) : content.description ? (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{content.description}</div>
            ) : null}
          </div>
        </div>

        {canWrite && (
          <div
            className="flex items-center justify-between gap-2 mb-4 rounded-lg border border-border/40 bg-muted/10 px-3 py-2"
            data-testid="embed-viewer-write-toolbar"
          >
            <div className="text-[11px] text-muted-foreground min-h-[16px]">
              {saveState.status === 'saving' && (
                <span className="inline-flex items-center gap-1" data-testid="embed-viewer-save-status-saving">
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                </span>
              )}
              {saveState.status === 'saved' && (
                <span className="inline-flex items-center gap-1 text-emerald-400" data-testid="embed-viewer-save-status-saved">
                  <Check className="w-3 h-3" /> Saved
                </span>
              )}
              {saveState.status === 'error' && (
                <span className="inline-flex items-center gap-1 text-rose-400" data-testid="embed-viewer-save-status-error">
                  <AlertTriangle className="w-3 h-3" /> {saveState.message || 'Save failed'}
                </span>
              )}
              {saveState.status === 'idle' && (
                isDirty
                  ? <span data-testid="embed-viewer-save-status-dirty">Unsaved changes</span>
                  : <span data-testid="embed-viewer-save-status-idle">All changes saved</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleReset}
                disabled={!isDirty || saveState.status === 'saving'}
                className="inline-flex items-center gap-1 rounded-md border border-border/50 px-2.5 py-1 text-[11px] hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="embed-viewer-reset-btn"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saveState.status === 'saving' || !edits.name?.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-500 px-3 py-1 text-[11px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="embed-viewer-save-btn"
              >
                <Save className="w-3 h-3" /> Save
              </button>
            </div>
          </div>
        )}

        {canWrite && isDirty && (
          <div className="mb-4 -mt-2">
            <label
              className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1"
              htmlFor="embed-viewer-save-note-input"
            >
              Save note <span className="normal-case text-muted-foreground/80">(optional — surfaces in history)</span>
            </label>
            <input
              id="embed-viewer-save-note-input"
              type="text"
              maxLength={280}
              value={saveNote}
              onChange={(e) => setSaveNote(e.target.value)}
              placeholder="e.g. Promoted staging changes, hotfixed legend colors"
              className="w-full bg-transparent border border-border/40 rounded-md px-2 py-1 text-xs outline-none focus:border-indigo-400/60"
              data-testid="embed-viewer-save-note-input"
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1">
            <Metric icon={Layers} label={resource === 'dashboard' ? 'Widgets' : 'Visuals'} value={widgetsToRender.length} />
            <Metric icon={Filter} label="Filters" value={filterCount} />
            <Metric label="Roles" value={(claims?.roles || []).join(', ') || '—'} />
            <Metric label="Capabilities" value={(claims?.capabilities || []).join(', ') || 'read'} />
          </div>
          {token && (
            <MentionInbox token={token} />
          )}
        </div>

        {meta.supportsWidgets && (
          <PageNavChrome
            style={pageNavStyle}
            pages={visiblePages}
            activeId={activePageId}
            onChange={setEmbedPageId}
            viewerMode={!canWrite}
            showHidden={canWrite}
          >
            {canWrite && widgetsToRender.length > 0 && (
              <WidgetGridCanvas
                widgets={widgetsToRender}
                onLayoutChange={onGridLayoutChange}
                token={token}
              />
            )}
            <WidgetList
              widgets={widgetsToRender}
              catalog={catalog}
              canWrite={canWrite}
              token={token}
              dashboardId={id}
              onPatch={patchWidget}
              onRemove={removeWidget}
              onAdd={addWidget}
              onDuplicate={duplicateWidget}
              onMove={moveWidget}
              onApplyPreset={applyPreset}
              onSaveAsPreset={saveAsPreset}
              customPresets={customPresets}
            />
          </PageNavChrome>
        )}

        {meta.supportsWidgets && (
          <VersionHistoryPanel
            open={showHistory}
            onToggle={toggleHistory}
            onRefresh={fetchVersions}
            versions={versions}
            loading={versionsLoading}
            canRestore={canWrite}
            restoreState={restoreState}
            onRestore={handleRestore}
            currentName={savedSnapshot.name}
            currentWidgetCount={savedSnapshot.widgets.length}
            token={token}
            dashboardId={id}
          />
        )}

        <div className="rounded-lg border border-border/40 bg-muted/10 p-4 text-xs text-muted-foreground leading-relaxed mt-6" data-testid="embed-viewer-canvas-placeholder">
          <div className="font-medium text-foreground mb-1">Ready to render</div>
          {canWrite
            ? `Rename, add, or remove widgets above and hit Save — updates route through the workspace-scoped Bearer JWT so no host secret leaves the browser. Drag-and-resize rolls out in the next drop.`
            : `The full canvas for this ${resource} will render here once the P1 authoring surface ships. For now this iframe surfaces the resolved metadata + viewer context so your host application can verify the drill-through link is wired correctly.`}
        </div>

        <div className="mt-4 text-[10px] text-muted-foreground font-mono break-all">
          resource_id: {content[meta.idField] || id}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border/40 bg-background/40 px-3 py-2">
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon className="w-3 h-3 text-muted-foreground/70" />}
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      </div>
      <div className="text-sm font-semibold truncate">{value}</div>
    </div>
  );
}

// WidgetGridCanvas extracted to /components/embed/WidgetGridCanvas.jsx (iter-222)


/**
 * Fetches and renders a lightweight synthetic preview for a widget.
 * Endpoint returns deterministic sample data — same measure produces
 * the same value on every request — so hosts see a stable "live" tile
 * until the real query engine is wired to the embed principal.
 */
function WidgetLivePreview_INTERNAL_REMOVED() { return null; }  // extracted → components/embed/WidgetLivePreview.jsx (iter-220)



/**
 * Compact preset dropdown — click "Presets" to reveal one-tap layout
 * choices. When the canvas isn't empty we confirm before replacing
 * so users don't lose in-progress work.
 */
/**
 * 40×20 mini-grid preview of a widgets[] layout. Pure CSS — no
 * canvas / images. Renders each widget as a coloured block positioned
 * over a normalized 12-col × 20-row proportional grid so the preview
 * always fits the dropdown row regardless of the source dimensions.
 */
// PresetMenu + PresetThumbnail extracted to /components/embed/PresetMenu.jsx (iter-221)



function WidgetList({ widgets, catalog, canWrite, token, dashboardId, onPatch, onRemove, onAdd, onDuplicate, onMove, onApplyPreset, onSaveAsPreset, customPresets }) {
  // Shared live-data cycle for every widget whose chart_config.is_live is
  // true. `useLiveWidgets` handles the batching, visibility-pause, and
  // in-flight cancellation. Hook lives above the early return so
  // React's rules-of-hooks stay satisfied.
  const {
    data: liveData,
    refreshedAt,
    error: liveError,
    refreshNow,
    liveCount,
  } = useLiveWidgets(token, widgets, { intervalSeconds: 60, enabled: !!token });
  const [refreshing, setRefreshing] = React.useState(false);
  const manualRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try { await refreshNow(); } finally { setRefreshing(false); }
  }, [refreshNow]);
  if (!widgets.length && !canWrite) return null;
  return (
    <div className="mt-2" data-testid="embed-viewer-widget-list">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Widgets
          {catalog && (
            <span
              className="ml-2 normal-case text-muted-foreground/80"
              data-testid="embed-viewer-catalog-counts"
            >
              · {catalog.measures.length} measures / {catalog.dimensions.length} dimensions available
            </span>
          )}
          {liveCount > 0 && (
            <span
              className="ml-2 normal-case text-emerald-300/80 font-mono"
              data-testid="embed-viewer-live-count"
            >
              · {liveCount} live · refreshed {refreshedAt ? new Date(refreshedAt).toLocaleTimeString() : '—'}
            </span>
          )}
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            {onApplyPreset && (
              <PresetMenu
                presets={LAYOUT_PRESETS}
                onApplyPreset={onApplyPreset}
                onSaveAsPreset={onSaveAsPreset}
                hasWidgets={widgets.length > 0}
                customPresets={customPresets}
              />
            )}
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-400/40 bg-indigo-500/10 px-2 py-1 text-[11px] text-indigo-300 hover:bg-indigo-500/20"
              data-testid="embed-viewer-add-widget-btn"
            >
              <Plus className="w-3 h-3" /> Add widget
            </button>
          </div>
        )}
      </div>
      {widgets.length ? (
        <ul className="space-y-1.5">
          {widgets.map((w, idx) => (
            <WidgetRow
              key={w.widget_id}
              widget={w}
              index={idx}
              total={widgets.length}
              canWrite={canWrite}
              catalog={catalog}
              token={token}
              dashboardId={dashboardId}
              onPatch={onPatch}
              onRemove={onRemove}
              onDuplicate={onDuplicate}
              onMove={onMove}
              liveRefreshedAt={refreshedAt}
              liveWidgetData={liveData?.[w.widget_id]}
              liveError={liveError}
              liveRefreshing={refreshing}
              onLiveRefresh={manualRefresh}
            />
          ))}
        </ul>
      ) : (
        <div
          className="rounded-md border border-dashed border-border/40 bg-background/40 px-3 py-4 text-xs text-muted-foreground text-center"
          data-testid="embed-viewer-widget-empty"
        >
          No widgets yet. Click <span className="font-medium text-foreground">Add widget</span> to create one.
        </div>
      )}
    </div>
  );
}

function WidgetRow({ widget, index, total, canWrite, catalog, token, dashboardId, onPatch, onRemove, onDuplicate, onMove, liveRefreshedAt, liveWidgetData, liveError, liveRefreshing, onLiveRefresh }) {
  const type = widget.widget_type || 'kpi';
  const measureId = widget.config?.measure_id || '';
  const dimensionId = widget.config?.dimension_id || '';
  // KPI cards only need a measure; charts/tables get a dimension picker too.
  const needsDimension = type !== 'kpi';
  const measureName = React.useMemo(() => (
    catalog?.measures?.find(m => m.id === measureId)?.name
  ), [catalog, measureId]);
  const patchConfig = (patch) => onPatch(widget.widget_id, { config: { ...(widget.config || {}), ...patch } });
  return (
    <li
      className="rounded-md border border-border/40 bg-background/40 px-2.5 py-2"
      data-testid={`embed-viewer-widget-row-${widget.widget_id}`}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" aria-hidden="true" />
        {canWrite && (
          <div className="flex flex-col shrink-0">
            <button
              type="button"
              onClick={() => onMove(widget.widget_id, 'up')}
              disabled={index === 0}
              className="text-muted-foreground/80 hover:text-indigo-300 disabled:opacity-30 disabled:hover:text-muted-foreground/80"
              title="Move up"
              aria-label="Move widget up"
              data-testid={`embed-viewer-widget-up-${widget.widget_id}`}
            >
              <ArrowUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => onMove(widget.widget_id, 'down')}
              disabled={index === total - 1}
              className="text-muted-foreground/80 hover:text-indigo-300 disabled:opacity-30 disabled:hover:text-muted-foreground/80"
              title="Move down"
              aria-label="Move widget down"
              data-testid={`embed-viewer-widget-down-${widget.widget_id}`}
            >
              <ArrowDown className="w-3 h-3" />
            </button>
          </div>
        )}
        {canWrite ? (
          <select
            className="bg-transparent text-[10px] uppercase tracking-wider text-muted-foreground border border-border/40 rounded px-1 py-0.5 shrink-0"
            value={type}
            onChange={(e) => onPatch(widget.widget_id, { widget_type: e.target.value })}
            data-testid={`embed-viewer-widget-type-${widget.widget_id}`}
            aria-label="Widget type"
          >
            {WIDGET_TYPE_OPTIONS.some(o => o.value === type)
              ? null
              : <option value={type}>{type}</option>}
            {WIDGET_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">{type}</span>
        )}
        {canWrite ? (
          <input
            className="flex-1 min-w-0 bg-transparent text-sm outline-none border-b border-transparent focus:border-border/50 py-0.5"
            value={widget.title || ''}
            placeholder="(untitled widget)"
            onChange={(e) => onPatch(widget.widget_id, { title: e.target.value })}
            data-testid={`embed-viewer-widget-title-input-${widget.widget_id}`}
            aria-label="Widget title"
          />
        ) : (
          <span className="flex-1 min-w-0 text-sm truncate">{widget.title || '(untitled widget)'}</span>
        )}
        <span className="text-[10px] text-muted-foreground font-mono shrink-0">
          {widget.w || 4}×{widget.h || 3}
        </span>
        <LiveRefreshBadge
          widget={widget}
          refreshedAt={liveRefreshedAt}
          refreshing={liveRefreshing}
          onRefresh={onLiveRefresh}
          error={liveWidgetData?.error || liveError}
        />
        <LiveDataMiniChart widget={widget} liveData={liveWidgetData} />
        {canWrite && (
          <button
            type="button"
            onClick={() => onDuplicate?.(widget.widget_id)}
            className="text-muted-foreground/80 hover:text-indigo-300 shrink-0"
            title="Duplicate widget"
            aria-label="Duplicate widget"
            data-testid={`embed-viewer-widget-duplicate-${widget.widget_id}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
        {canWrite && (
          <button
            type="button"
            onClick={() => onRemove(widget.widget_id)}
            className="text-muted-foreground/80 hover:text-rose-400 shrink-0"
            title="Remove widget"
            aria-label="Remove widget"
            data-testid={`embed-viewer-widget-delete-${widget.widget_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {canWrite ? (
        <div className="mt-1.5 pl-6 flex items-center gap-2 flex-wrap">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Measure</label>
          <select
            className="bg-transparent border border-border/40 rounded px-1.5 py-0.5 text-[11px] max-w-[220px]"
            value={measureId}
            onChange={(e) => patchConfig({ measure_id: e.target.value || null })}
            data-testid={`embed-viewer-widget-measure-${widget.widget_id}`}
            aria-label="Widget measure"
          >
            <option value="">— none —</option>
            {(catalog?.measures || []).map(m => (
              <option key={m.id} value={m.id}>{m.entity_name ? `${m.entity_name} · ${m.name}` : m.name}</option>
            ))}
          </select>
          {needsDimension && (
            <>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Dimension</label>
              <select
                className="bg-transparent border border-border/40 rounded px-1.5 py-0.5 text-[11px] max-w-[220px]"
                value={dimensionId}
                onChange={(e) => patchConfig({ dimension_id: e.target.value || null })}
                data-testid={`embed-viewer-widget-dimension-${widget.widget_id}`}
                aria-label="Widget dimension"
              >
                <option value="">— none —</option>
                {(catalog?.dimensions || []).map(d => (
                  <option key={d.id} value={d.id}>{d.entity_name ? `${d.entity_name} · ${d.name}` : d.name}</option>
                ))}
              </select>
            </>
          )}
        </div>
      ) : (measureId || dimensionId) ? (
        <div className="mt-1.5 pl-6 flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
          {measureId && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-[1px] text-emerald-300"
              data-testid={`embed-viewer-widget-measure-badge-${widget.widget_id}`}
            >
              measure: {measureName || measureId}
            </span>
          )}
          {dimensionId && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-sky-400/30 bg-sky-500/10 px-1.5 py-[1px] text-sky-300"
              data-testid={`embed-viewer-widget-dimension-badge-${widget.widget_id}`}
            >
              by: {dimensionId}
            </span>
          )}
        </div>
      ) : null}
      {canWrite && (
        <WidgetConfigDrawer widget={widget} onPatchConfig={patchConfig} token={token} />
      )}
      {token && dashboardId && (
        <WidgetComments widget={widget} token={token} dashboardId={dashboardId} canWrite={canWrite} />
      )}
    </li>
  );
}

/**
 * Widget config drawer — collapsible per-widget panel with format
 * (number pattern), filters (list of `{key, op, value}` tuples), and
 * axis label overrides. All controls persist into `widget.config` so
 * the existing save flow picks them up with no schema change.
 */
/**
 * Per-widget comment thread. Collapsed by default with an unread-style
 * count badge; expands to show comments + (for `write` sessions) an
 * inline compose box. Uses the bulk count endpoint to avoid one
 * request per widget on mount.
 */
function WidgetComments({ widget, token, dashboardId, canWrite }) {
  const [open, setOpen] = React.useState(false);
  const [comments, setComments] = React.useState(null);
  const [count, setCount] = React.useState(0);
  const [body, setBody] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const [mentionQuery, setMentionQuery] = React.useState(null); // null when not composing @; string otherwise
  const [mentionResults, setMentionResults] = React.useState([]);
  const [mentionIdx, setMentionIdx] = React.useState(0);
  const textareaRef = React.useRef(null);
  // Best-effort initial count — bulk endpoint keeps this cheap.
  React.useEffect(() => {
    if (!token || !dashboardId) return undefined;
    let alive = true;
    fetch(`${API}/api/embed/v2/dashboards/${encodeURIComponent(dashboardId)}/widget-comment-counts`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(b => {
        if (!alive || !b?.counts) return;
        setCount(b.counts[widget.widget_id] || 0);
      })
      .catch(() => { /* best-effort */ });
    return () => { alive = false; };
  }, [token, dashboardId, widget.widget_id]);
  const load = React.useCallback(async () => {
    if (!token || !dashboardId) return;
    try {
      const res = await fetch(
        `${API}/api/embed/v2/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widget.widget_id)}/comments`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const b = await res.json();
      setComments(b?.data || []);
      setCount((b?.data || []).length);
    } catch { /* silent */ }
  }, [token, dashboardId, widget.widget_id]);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && comments === null) await load();
  };
  const post = async () => {
    const b = body.trim();
    if (!b || !token) return;
    setPosting(true);
    try {
      const res = await fetch(
        `${API}/api/embed/v2/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widget.widget_id)}/comments`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: b }),
        },
      );
      if (res.ok) {
        setBody('');
        setMentionQuery(null);
        setMentionResults([]);
        await load();
      }
    } finally { setPosting(false); }
  };
  // Detect an active @-token immediately before the caret so we can
  // pop a suggestion list. Only fires when the user is actively typing
  // a handle (no whitespace in between).
  const detectMention = React.useCallback((text, caret) => {
    const upto = text.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([A-Za-z0-9._-]*)$/);
    if (m) return m[1];
    return null;
  }, []);
  const handleBodyChange = (e) => {
    const val = e.target.value;
    setBody(val);
    const caret = e.target.selectionStart || val.length;
    const q = detectMention(val, caret);
    setMentionQuery(q);
    setMentionIdx(0);
  };
  // Fetch mention suggestions when a query is active. Debounced by the
  // React re-render cycle; endpoint returns quickly since it's a
  // single mongo find on the workspace_users collection.
  React.useEffect(() => {
    if (mentionQuery === null || !token) {
      setMentionResults([]);
      return undefined;
    }
    let alive = true;
    const url = `${API}/api/embed/v2/workspace/mentionable-users?limit=6${mentionQuery ? `&q=${encodeURIComponent(mentionQuery)}` : ''}`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(b => { if (alive && b?.data) setMentionResults(b.data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [mentionQuery, token]);
  const applyMention = (handle) => {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? body.length;
    const before = body.slice(0, caret);
    const after = body.slice(caret);
    // Replace the active @token (everything from the last @ before the
    // caret) with @handle + space so the caret sits ready for the next
    // word.
    const cleaned = before.replace(/@([A-Za-z0-9._-]*)$/, `@${handle} `);
    const next = cleaned + after;
    setBody(next);
    setMentionQuery(null);
    setMentionResults([]);
    // Restore focus + caret to the end of the inserted handle.
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = cleaned.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };
  const onKeyDown = (e) => {
    if (mentionQuery !== null && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % mentionResults.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + mentionResults.length) % mentionResults.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyMention(mentionResults[mentionIdx].sub);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        setMentionResults([]);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      post();
    }
  };
  return (
    <div className="mt-1.5 pl-6" data-testid={`embed-viewer-widget-comments-${widget.widget_id}`}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-indigo-300"
        data-testid={`embed-viewer-widget-comments-toggle-${widget.widget_id}`}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <MessageCircle className="w-3 h-3" /> Comments
        {count > 0 && (
          <span
            className="ml-1 rounded-full bg-amber-500/20 text-amber-300 px-1.5 py-[1px] text-[9px] normal-case tracking-normal"
            data-testid={`embed-viewer-widget-comments-count-${widget.widget_id}`}
          >
            {count}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-border/40 bg-background/40 p-2 space-y-1.5" data-testid={`embed-viewer-widget-comments-body-${widget.widget_id}`}>
          {comments === null ? (
            <div className="text-[10px] text-muted-foreground">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-[10px] text-muted-foreground italic">No comments yet.</div>
          ) : (
            comments.map(c => (
              <div key={c.comment_id} className="rounded border border-border/30 bg-background/30 px-2 py-1">
                <CommentBody body={c.body} mentions={c.mentions || []} testid={`embed-viewer-widget-comment-body-${c.comment_id}`} />
                <div className="text-[9px] text-muted-foreground mt-0.5">
                  <span className="font-mono">{c.author}</span>
                  {' · '}
                  <span>{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                </div>
              </div>
            ))
          )}
          {canWrite && (
            <div className="relative flex items-start gap-1 pt-1 border-t border-border/30">
              <textarea
                ref={textareaRef}
                value={body}
                onChange={handleBodyChange}
                onKeyDown={onKeyDown}
                placeholder="Add a comment… use @ to mention teammates"
                rows={2}
                className="flex-1 min-w-0 bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60 resize-none"
                data-testid={`embed-viewer-widget-comment-input-${widget.widget_id}`}
              />
              <button
                type="button"
                onClick={post}
                disabled={!body.trim() || posting}
                className="rounded-md bg-indigo-500 px-2 py-1 text-[10px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                data-testid={`embed-viewer-widget-comment-post-${widget.widget_id}`}
              >
                {posting ? '…' : 'Post'}
              </button>
              {mentionQuery !== null && mentionResults.length > 0 && (
                <div
                  className="absolute z-10 top-full left-0 mt-1 w-56 rounded-md border border-border/40 bg-background shadow-lg overflow-hidden"
                  data-testid={`embed-viewer-widget-comment-mention-list-${widget.widget_id}`}
                >
                  {mentionResults.map((u, i) => (
                    <button
                      key={u.sub}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); applyMention(u.sub); }}
                      onMouseEnter={() => setMentionIdx(i)}
                      className={`block w-full text-left px-2 py-1 text-[11px] ${i === mentionIdx ? 'bg-indigo-500/20 text-indigo-100' : 'hover:bg-indigo-500/10 text-foreground'}`}
                      data-testid={`embed-viewer-widget-comment-mention-item-${widget.widget_id}-${u.sub}`}
                    >
                      <span className="font-mono text-indigo-300">@{u.sub}</span>
                      {u.display_name && u.display_name !== u.sub && (
                        <span className="ml-1 text-muted-foreground">· {u.display_name}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Renders a comment body, converting `@handle` tokens into small
 * highlighted pills so mentions are visually distinct from prose.
 * Kept intentionally minimal — no XSS surface because the input never
 * touches innerHTML; we only split-and-map React children.
 */
function CommentBody({ body, mentions, testid }) {
  if (!body) return null;
  // Same regex shape as the backend so what the author wrote is what
  // shows up. The set of handles the backend extracted is passed in
  // (`mentions`) so we only pill those (mistyped @foobar with no
  // matching user still shows as plain text).
  const known = new Set((mentions || []).map((m) => m.toLowerCase()));
  const parts = [];
  const re = /(^|[\s(\[,;:])@([A-Za-z0-9][A-Za-z0-9._-]{0,63})/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(body)) !== null) {
    const leading = m[1] || '';
    const handle = m[2];
    const tokenStart = m.index + leading.length;
    if (tokenStart > last) {
      parts.push(<span key={`t-${idx++}`}>{body.slice(last, tokenStart)}</span>);
    }
    if (known.has(handle.toLowerCase())) {
      parts.push(
        <span
          key={`m-${idx++}`}
          className="inline-flex items-center rounded-sm bg-indigo-500/20 text-indigo-300 px-1 font-mono text-[10px]"
        >
          @{handle}
        </span>,
      );
    } else {
      parts.push(<span key={`p-${idx++}`}>@{handle}</span>);
    }
    last = tokenStart + handle.length + 1; // 1 for '@'
  }
  if (last < body.length) parts.push(<span key={`t-${idx++}`}>{body.slice(last)}</span>);
  return (
    <div className="text-[11px] whitespace-pre-wrap break-words" data-testid={testid}>
      {parts}
    </div>
  );
}


function WidgetConfigDrawer({ widget, onPatchConfig, token }) {
  const [open, setOpen] = React.useState(false);
  const cfg = widget.config || {};
  const filters = Array.isArray(cfg.filters) ? cfg.filters : [];
  const type = widget.widget_type || 'kpi';
  const showsAxis = type === 'bar_chart' || type === 'line_chart';
  const filterCount = filters.filter(f => f.key || f.value).length;
  return (
    <div className="mt-1.5 pl-6" data-testid={`embed-viewer-widget-config-${widget.widget_id}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-indigo-300"
        data-testid={`embed-viewer-widget-config-toggle-${widget.widget_id}`}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Settings2 className="w-3 h-3" /> Config
        {(cfg.format || filterCount > 0 || cfg.x_axis_label || cfg.y_axis_label || cfg.orientation) && (
          <span className="ml-1 rounded-full bg-indigo-500/20 text-indigo-200 px-1.5 py-[1px] text-[9px] normal-case tracking-normal">
            customized
          </span>
        )}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-border/40 bg-background/40 p-2 space-y-2">
          {/* Live preview at the top so authors can see the effect of
              format/filter changes without hunting for the canvas tile. */}
          {widget.config?.measure_id && (
            <div
              className="rounded border border-border/30 bg-background/60 p-2"
              data-testid={`embed-viewer-widget-config-preview-${widget.widget_id}`}
            >
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">
                Live preview
              </div>
              <WidgetLivePreview widget={widget} token={token} />
            </div>
          )}
          {/* Time range (rolling window) */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time range</span>
            <select
              value={cfg.time_range || 'all'}
              onChange={(e) => onPatchConfig({ time_range: e.target.value === 'all' ? null : e.target.value })}
              className="w-full mt-0.5 bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
              data-testid={`embed-viewer-widget-timerange-${widget.widget_id}`}
              aria-label="Rolling time range"
            >
              <option value="all">All time</option>
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_90_days">Last 90 days</option>
              <option value="last_year">Last 12 months</option>
              <option value="ytd">Year to date</option>
            </select>
          </label>

          {/* Format */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Number format</span>
            <input
              type="text"
              value={cfg.format || ''}
              onChange={(e) => onPatchConfig({ format: e.target.value || null })}
              placeholder="e.g. $0,0.00 · 0.0% · 0,0"
              className="w-full mt-0.5 bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
              data-testid={`embed-viewer-widget-format-${widget.widget_id}`}
              aria-label="Number format"
            />
          </label>

          {/* Axis (charts only) */}
          {showsAxis && (
            <div className="grid grid-cols-3 gap-2">
              <label>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">X axis</span>
                <input
                  type="text"
                  value={cfg.x_axis_label || ''}
                  onChange={(e) => onPatchConfig({ x_axis_label: e.target.value || null })}
                  className="w-full mt-0.5 bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                  data-testid={`embed-viewer-widget-x-axis-${widget.widget_id}`}
                  aria-label="X axis label"
                />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Y axis</span>
                <input
                  type="text"
                  value={cfg.y_axis_label || ''}
                  onChange={(e) => onPatchConfig({ y_axis_label: e.target.value || null })}
                  className="w-full mt-0.5 bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                  data-testid={`embed-viewer-widget-y-axis-${widget.widget_id}`}
                  aria-label="Y axis label"
                />
              </label>
              {type === 'bar_chart' && (
                <label>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Orient</span>
                  <select
                    value={cfg.orientation || 'vertical'}
                    onChange={(e) => onPatchConfig({ orientation: e.target.value })}
                    className="w-full mt-0.5 bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                    data-testid={`embed-viewer-widget-orientation-${widget.widget_id}`}
                    aria-label="Bar orientation"
                  >
                    <option value="vertical">Vertical</option>
                    <option value="horizontal">Horizontal</option>
                  </select>
                </label>
              )}
            </div>
          )}

          {/* Filters */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Filters</span>
              <button
                type="button"
                onClick={() => onPatchConfig({
                  filters: [...filters, { key: '', op: 'equals', value: '' }],
                })}
                className="text-[10px] text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-0.5"
                data-testid={`embed-viewer-widget-filter-add-${widget.widget_id}`}
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {filters.length === 0 ? (
              <div className="text-[10px] text-muted-foreground italic">No filters applied.</div>
            ) : (
              <ul className="space-y-1" data-testid={`embed-viewer-widget-filter-list-${widget.widget_id}`}>
                {filters.map((f, i) => (
                  <li key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-1 items-center">
                    <input
                      type="text"
                      value={f.key || ''}
                      placeholder="field"
                      onChange={(e) => {
                        const next = filters.slice();
                        next[i] = { ...next[i], key: e.target.value };
                        onPatchConfig({ filters: next });
                      }}
                      className="bg-transparent border border-border/40 rounded px-1.5 py-0.5 text-[10px] font-mono outline-none focus:border-indigo-400/60"
                      data-testid={`embed-viewer-widget-filter-key-${widget.widget_id}-${i}`}
                    />
                    <select
                      value={f.op || 'equals'}
                      onChange={(e) => {
                        const next = filters.slice();
                        next[i] = { ...next[i], op: e.target.value };
                        onPatchConfig({ filters: next });
                      }}
                      className="bg-transparent border border-border/40 rounded px-1 py-0.5 text-[10px] outline-none focus:border-indigo-400/60"
                      data-testid={`embed-viewer-widget-filter-op-${widget.widget_id}-${i}`}
                    >
                      <option value="equals">=</option>
                      <option value="not_equals">≠</option>
                      <option value="in">in</option>
                      <option value="gt">&gt;</option>
                      <option value="lt">&lt;</option>
                      <option value="contains">contains</option>
                    </select>
                    <input
                      type="text"
                      value={f.value || ''}
                      placeholder="value"
                      onChange={(e) => {
                        const next = filters.slice();
                        next[i] = { ...next[i], value: e.target.value };
                        onPatchConfig({ filters: next });
                      }}
                      className="bg-transparent border border-border/40 rounded px-1.5 py-0.5 text-[10px] outline-none focus:border-indigo-400/60"
                      data-testid={`embed-viewer-widget-filter-value-${widget.widget_id}-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = filters.filter((_, k) => k !== i);
                        onPatchConfig({ filters: next });
                      }}
                      className="text-muted-foreground/80 hover:text-rose-400"
                      title="Remove filter"
                      aria-label="Remove filter"
                      data-testid={`embed-viewer-widget-filter-remove-${widget.widget_id}-${i}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Version history panel — collapsed by default, opens to show the last
 * N pre-save snapshots. Read-only viewers get an audit view; write-
 * capable authors get a "Restore" button on each row.
 *
 * `versions === null` means "not fetched yet" (before first toggle);
 * `versions === []` means "server said no history". Kept distinct so
 * the UI can differentiate "loading first time" from "empty result".
 */
function VersionHistoryPanel({
  open, onToggle, onRefresh, versions, loading, canRestore, restoreState, onRestore,
  currentName, currentWidgetCount, token, dashboardId,
}) {
  // Client-side keyword filter over name + note + saved_by. Cheap on
  // the small (≤20) datasets this endpoint returns; keeps history
  // discoverable even after weeks of edits.
  const [query, setQuery] = React.useState('');
  // Version-id → full diff object (fetched lazily on "Compare" click).
  const [expandedDiffs, setExpandedDiffs] = React.useState({});
  const [diffLoading, setDiffLoading] = React.useState({});
  const filteredVersions = React.useMemo(() => {
    if (!Array.isArray(versions)) return versions;
    const q = query.trim().toLowerCase();
    if (!q) return versions;
    return versions.filter(v => {
      const hay = [v.name, v.note, v.saved_by].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [versions, query]);

  const toggleCompare = async (versionId) => {
    if (expandedDiffs[versionId]) {
      // Toggle off — clear so the caret flips back and the details
      // section disappears.
      setExpandedDiffs(s => { const n = { ...s }; delete n[versionId]; return n; });
      return;
    }
    if (!token || !dashboardId) return;
    setDiffLoading(s => ({ ...s, [versionId]: true }));
    try {
      const url = `${API}/api/embed/v2/dashboards/${encodeURIComponent(dashboardId)}/versions/${encodeURIComponent(versionId)}?diff=true`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const body = await res.json();
        setExpandedDiffs(s => ({ ...s, [versionId]: body?.diff || {} }));
      }
    } catch { /* silent — the compare toggle just stays off */ }
    finally { setDiffLoading(s => ({ ...s, [versionId]: false })); }
  };
  /**
   * Return a short human-readable "restore diff" describing what will
   * change if the author picks Restore on this row. Returns an array
   * of chip labels (empty when the snapshot is functionally equal to
   * the current state).
   */
  const restoreDiff = (v) => {
    const chips = [];
    if (typeof currentName === 'string' && v.name && v.name !== currentName) {
      chips.push({ tone: 'name', label: `rename → ${v.name.length > 20 ? v.name.slice(0, 20) + '…' : v.name}` });
    }
    if (typeof currentWidgetCount === 'number' && typeof v.widget_count === 'number' && v.widget_count !== currentWidgetCount) {
      const delta = v.widget_count - currentWidgetCount;
      chips.push({
        tone: delta > 0 ? 'add' : 'remove',
        label: `${delta > 0 ? '+' : ''}${delta} widget${Math.abs(delta) === 1 ? '' : 's'}`,
      });
    }
    return chips;
  };
  return (
    <div className="mt-6" data-testid="embed-viewer-history-panel">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 rounded-md border border-border/40 bg-background/40 px-3 py-2 hover:bg-muted/20 text-left"
        data-testid="embed-viewer-history-toggle"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
          <History className="w-3.5 h-3.5" /> Save history
          {Array.isArray(versions) && (
            <span className="rounded-full bg-muted/40 px-1.5 py-[1px] text-[9px] normal-case text-muted-foreground/80">
              {versions.length}
            </span>
          )}
        </span>
        <span className="text-[10px] text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-border/40 bg-background/40 p-2" data-testid="embed-viewer-history-body">
          {Array.isArray(versions) && versions.length > 0 && (
            <div className="mb-2 px-1">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search notes, names, or authors…"
                className="w-full bg-transparent border border-border/40 rounded px-2 py-1 text-[11px] outline-none focus:border-indigo-400/60"
                data-testid="embed-viewer-history-search"
                aria-label="Search history"
              />
            </div>
          )}
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[10px] text-muted-foreground">
              {loading
                ? 'Loading history…'
                : (versions?.length
                    ? (query
                        ? `${filteredVersions.length} of ${versions.length} matching “${query}”`
                        : `Last ${versions.length} saves — newest first`)
                    : 'No prior versions yet — hit Save to start capturing history.')}
            </div>
            <div className="flex items-center gap-2">
              {restoreState.status === 'restoring' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="embed-viewer-restore-status-restoring">
                  <Loader2 className="w-3 h-3 animate-spin" /> Restoring…
                </span>
              )}
              {restoreState.status === 'restored' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400" data-testid="embed-viewer-restore-status-restored">
                  <Check className="w-3 h-3" /> Restored
                </span>
              )}
              {restoreState.status === 'error' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-rose-400" data-testid="embed-viewer-restore-status-error">
                  <AlertTriangle className="w-3 h-3" /> {restoreState.message || 'Restore failed'}
                </span>
              )}
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="text-muted-foreground/80 hover:text-indigo-300 disabled:opacity-40"
                title="Refresh history"
                aria-label="Refresh history"
                data-testid="embed-viewer-history-refresh-btn"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          {Array.isArray(filteredVersions) && filteredVersions.length > 0 && (
            <ul className="space-y-1" data-testid="embed-viewer-history-list">
              {filteredVersions.map(v => {
                const diffs = restoreDiff(v);
                const expandedDiff = expandedDiffs[v.version_id];
                return (
                  <li
                    key={v.version_id}
                    className="rounded border border-border/30 bg-background/30 px-2 py-1.5"
                    data-testid={`embed-viewer-history-row-${v.version_id}`}
                  >
                    <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{v.name || '(untitled)'}</div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span>{v.saved_at ? new Date(v.saved_at).toLocaleString() : '—'}</span>
                        <span>·</span>
                        <span className="font-mono">by {v.saved_by || 'unknown'}</span>
                        <span>·</span>
                        <span>{v.widget_count ?? 0} widgets</span>
                      </div>
                      {v.note && (
                        <div
                          className="mt-1 text-[10px] italic text-muted-foreground/90 line-clamp-2"
                          data-testid={`embed-viewer-history-note-${v.version_id}`}
                        >
                          “{v.note}”
                        </div>
                      )}
                      {diffs.length > 0 && (
                        <div
                          className="mt-1 flex flex-wrap gap-1"
                          data-testid={`embed-viewer-history-diff-${v.version_id}`}
                        >
                          {diffs.map((d, i) => (
                            <span
                              key={i}
                              className={
                                'inline-flex items-center rounded-full px-1.5 py-[1px] text-[9px] font-medium ' +
                                (d.tone === 'add'
                                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30'
                                  : d.tone === 'remove'
                                    ? 'bg-rose-500/15 text-rose-300 border border-rose-400/30'
                                    : 'bg-amber-500/15 text-amber-300 border border-amber-400/30')
                              }
                            >
                              {d.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 mt-0.5">
                      <button
                        type="button"
                        onClick={() => toggleCompare(v.version_id)}
                        disabled={diffLoading[v.version_id]}
                        className="rounded-md border border-border/50 px-2 py-1 text-[10px] hover:bg-muted/40 disabled:opacity-40"
                        title="Inspect what will change if you restore this snapshot"
                        data-testid={`embed-viewer-history-compare-${v.version_id}`}
                      >
                        {expandedDiffs[v.version_id] ? 'Hide diff' : (diffLoading[v.version_id] ? '…' : 'Compare')}
                      </button>
                      {canRestore && (
                        <button
                          type="button"
                          onClick={() => onRestore(v.version_id)}
                          disabled={restoreState.status === 'restoring' || diffs.length === 0}
                          title={diffs.length === 0 ? 'This snapshot matches the current state' : 'Restore this snapshot'}
                          className="rounded-md border border-border/50 px-2 py-1 text-[10px] hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`embed-viewer-history-restore-${v.version_id}`}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                    </div>
                    {expandedDiff && (
                      <div
                        className="mt-2 rounded border border-border/30 bg-background/40 p-2 text-[10px] space-y-1"
                        data-testid={`embed-viewer-history-diff-detail-${v.version_id}`}
                      >
                        {expandedDiff.name_changed && (
                          <div>
                            <span className="text-muted-foreground">Name:</span>{' '}
                            <span className="text-rose-300 line-through">{expandedDiff.current_name || '(empty)'}</span>{' → '}
                            <span className="text-emerald-300">{expandedDiff.snapshot_name || '(empty)'}</span>
                          </div>
                        )}
                        {expandedDiff.widgets_added?.length > 0 && (
                          <div>
                            <span className="text-emerald-300">+ Adds:</span>{' '}
                            {expandedDiff.widgets_added.map(w => (
                              <span key={w.widget_id} className="inline-block mr-1 rounded bg-emerald-500/15 px-1 py-[1px]">
                                {w.title || w.widget_id} <span className="opacity-60">({w.widget_type})</span>
                              </span>
                            ))}
                          </div>
                        )}
                        {expandedDiff.widgets_removed?.length > 0 && (
                          <div>
                            <span className="text-rose-300">− Removes:</span>{' '}
                            {expandedDiff.widgets_removed.map(w => (
                              <span key={w.widget_id} className="inline-block mr-1 rounded bg-rose-500/15 px-1 py-[1px]">
                                {w.title || w.widget_id} <span className="opacity-60">({w.widget_type})</span>
                              </span>
                            ))}
                          </div>
                        )}
                        {expandedDiff.widgets_renamed?.length > 0 && (
                          <div>
                            <span className="text-amber-300">↻ Renames:</span>{' '}
                            {expandedDiff.widgets_renamed.map(r => (
                              <span key={r.widget_id} className="inline-block mr-1 rounded bg-amber-500/15 px-1 py-[1px]">
                                {r.from || '(empty)'} → {r.to || '(empty)'}
                              </span>
                            ))}
                          </div>
                        )}
                        {!expandedDiff.name_changed
                          && !expandedDiff.widgets_added?.length
                          && !expandedDiff.widgets_removed?.length
                          && !expandedDiff.widgets_renamed?.length
                          && (
                            <div className="text-muted-foreground italic">
                              This snapshot matches the current state exactly.
                            </div>
                          )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}



/**
 * Mention inbox — compact bell/badge showing the number of unread
 * @-mentions in this workspace for the current viewer. Click opens a
 * dropdown listing the most recent mentions (author + short preview
 * + relative time). Individual rows or the "Mark all read" button
 * clear the unread badge.
 *
 * Auto-polls every 30s so mentions from teammates surface without a
 * page reload. Never blocks the surrounding UI — errors are silent.
 */
function MentionInbox({ token }) {
  const [open, setOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/embed/v2/mentions?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) return;
      const b = await r.json();
      setUnread(b?.unread || 0);
      setRows(b?.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);
  React.useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);
  const markAll = async () => {
    try {
      const r = await fetch(`${API}/api/embed/v2/mentions/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) await load();
    } catch { /* silent */ }
  };
  const markOne = async (mentionId) => {
    try {
      const r = await fetch(`${API}/api/embed/v2/mentions/${encodeURIComponent(mentionId)}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) await load();
    } catch { /* silent */ }
  };
  return (
    <div className="relative" data-testid="embed-viewer-mention-inbox">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center gap-1.5 rounded-md border border-border/50 bg-background/40 px-2.5 py-1.5 text-[11px] hover:bg-muted/40"
        data-testid="embed-viewer-mention-inbox-toggle"
        aria-label="Mentions inbox"
      >
        <Inbox className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Mentions</span>
        {unread > 0 && (
          <span
            className="ml-1 min-w-[16px] rounded-full bg-rose-500 px-1.5 py-[1px] text-[10px] font-semibold text-white leading-none"
            data-testid="embed-viewer-mention-inbox-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 w-80 max-h-96 overflow-auto rounded-md border border-border/40 bg-background shadow-xl"
          data-testid="embed-viewer-mention-inbox-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/40 sticky top-0 bg-background">
            <div className="text-xs font-semibold">Mentions</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  className="text-[10px] text-indigo-300 hover:text-indigo-100"
                  data-testid="embed-viewer-mention-inbox-mark-all"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 text-muted-foreground hover:text-foreground"
                aria-label="Close"
                data-testid="embed-viewer-mention-inbox-close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
          {loading && rows.length === 0 ? (
            <div className="p-3 text-[11px] text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-3 text-[11px] text-muted-foreground italic" data-testid="embed-viewer-mention-inbox-empty">
              You have no mentions yet.
            </div>
          ) : (
            <ul className="divide-y divide-border/30">
              {rows.map((m) => (
                <li
                  key={m.mention_id}
                  className={`px-3 py-2 text-[11px] ${m.read_at ? 'opacity-60' : ''}`}
                  data-testid={`embed-viewer-mention-inbox-row-${m.mention_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground">
                        <span className="font-mono text-indigo-300">@{m.author}</span>
                        <span className="text-muted-foreground"> · widget {m.widget_id}</span>
                      </div>
                      <div className="mt-0.5 text-muted-foreground line-clamp-2">
                        {m.body}
                      </div>
                      <div className="mt-1 text-[9px] text-muted-foreground/70">
                        {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                      </div>
                    </div>
                    {!m.read_at && (
                      <button
                        type="button"
                        onClick={() => markOne(m.mention_id)}
                        className="shrink-0 text-[9px] text-indigo-300 hover:text-indigo-100"
                        data-testid={`embed-viewer-mention-inbox-mark-${m.mention_id}`}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
